import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { BsX } from 'react-icons/bs';
import './RaiseAmountPopup.css';

interface RaiseAmountPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onRaise: (amount: number) => void;
  bigBlind?: number;
  potSize?: number; // Current pot size for pot-sized suggestions
  toCall?: number;  // Amount needed to call
  maxAmount?: number; // Max stack/effective stack for All-in
}

const RaiseAmountPopup: React.FC<RaiseAmountPopupProps> = ({ isOpen, onClose, onRaise, bigBlind, potSize, toCall, maxAmount }) => {
  const [amount, setAmount] = useState<string>('');
  const allowDecimals = typeof bigBlind === 'number' && !Number.isInteger(bigBlind) ? true : false;

  const formatValue = (num: number) => allowDecimals ? String(Number(num.toFixed(2))) : String(Math.round(num));

  useEffect(() => {
    if (isOpen) {
      // Clear input on open for easier typing; suggestions/buttons will set explicitly
      setAmount('');
    }
  }, [isOpen, bigBlind]);

  const handleRaise = () => {
    const raiseAmount = parseFloat(amount);
    if (!isNaN(raiseAmount) && raiseAmount > 0) {
      const finalAmount = allowDecimals ? raiseAmount : Math.round(raiseAmount);
      onRaise(finalAmount);
      onClose(); // Close the modal on successful raise
    } else {
      alert('Please enter a valid raise amount.');
    }
  };

  const getSuggestedAmounts = () => {
    const suggestions: Array<{ label: string; value: number }> = [];
    if (potSize && potSize > 0) {
      suggestions.push({ label: '1/2 Pot', value: potSize / 2 });
      suggestions.push({ label: 'Pot', value: potSize });
    }
    if (bigBlind && bigBlind > 0) {
      suggestions.push({ label: '3x', value: bigBlind * 3 });
      suggestions.push({ label: '4x', value: bigBlind * 4 });
    }
    if (typeof maxAmount === 'number' && maxAmount > 0) {
      suggestions.push({ label: 'All-in', value: maxAmount });
    }
    return suggestions;
  };

  // Numpad helpers (mobile friendly)
  const appendChar = (ch: string) => {
    setAmount(prev => {
      if (ch === '.') {
        if (!allowDecimals) return prev; // ignore decimals when not allowed
        if (!prev) return '0.';
        if (prev.includes('.')) return prev; // only one dot
        return prev + '.';
      }
      // Avoid leading zeros like 000
      if (prev === '0' && ch !== '.') return ch;
      // Limit to 2 decimals visually (not strictly enforced)
      const [i, d] = prev.split('.');
      if (allowDecimals && d && d.length >= 2) return prev; // keep at most 2 decimals
      if (!allowDecimals) {
        // overwrite if prev is empty or 0 for integer mode
        return (prev || '') + ch;
      }
      return (prev || '') + ch;
    });
  };

  const backspace = () => setAmount(prev => prev.slice(0, -1));
  const clearAll = () => setAmount('');

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="raise-dialog-overlay" />
        <Dialog.Content className="raise-dialog-content">
          <Dialog.Title className="raise-dialog-title">Enter Raise Amount</Dialog.Title>
          
          {/* Display + context row */}
          <div className="raise-input-container">
            <div className="raise-amount-display">
              <span className="currency">$</span>
              <span className="value">{amount || '0'}</span>
            </div>
            {(typeof toCall === 'number' || typeof potSize === 'number') && (
              <div className="raise-meta">
                {typeof toCall === 'number' && <span>To call: ${toCall.toFixed(2)}</span>}
                {typeof potSize === 'number' && <span>Pot: ${potSize.toFixed(2)}</span>}
              </div>
            )}
          </div>

          {/* Quick actions */}
          <div className="suggested-amounts">
            {getSuggestedAmounts().map(suggestion => (
              <button
                key={suggestion.label}
                className="suggested-amount-btn"
                onClick={() => setAmount(formatValue(suggestion.value))}
              >
                {suggestion.label}
              </button>
            ))}
          </div>

          {/* Numpad */}
          <div className="numpad-grid">
            {['1','2','3','4','5','6','7','8','9'].map(n => (
              <button key={n} className="numpad-key" onClick={() => appendChar(n)}>{n}</button>
            ))}
            <button className="numpad-key" onClick={() => appendChar('.')}>.</button>
            <button className="numpad-key" onClick={() => appendChar('0')}>0</button>
            <button className="numpad-key backspace" onClick={backspace}>⌫</button>
            <button className="numpad-key clear" onClick={clearAll}>CLR</button>
          </div>

          <div className="raise-dialog-actions">
            <button className="raise-dialog-button raise" onClick={handleRaise}>Raise</button>
          </div>

          <Dialog.Close asChild>
            <button className="raise-dialog-close" aria-label="Close">
              <BsX />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default RaiseAmountPopup; 